/**
 * Quick Add functionality for product cards
 * Handles color selection, size display on hover, and add to cart
 */

class QuickAddCard {
  constructor(cardElement) {
    this.card = cardElement;
    this.productData = this.getProductData();
    this.selectedColor = null;
    this.isLinkVariant = false;
    
    if (!this.productData) return;
    
    this.init();
  }

  init() {
    this.initializeDefaultColor();
    this.setupColorButtons();
    this.setupImageHover();

    if (window.innerWidth < 768) {
      this.showSizes();
    }
  }

  getProductData() {
    const jsonScript = this.card.querySelector('[data-product-json]');
    if (!jsonScript) return null;
    
    try {
      return JSON.parse(jsonScript.textContent);
    } catch (e) {
      console.error('Quick Add: Erro ao fazer parse dos dados do produto:', e);
      return null;
    }
  }

  initializeDefaultColor() {
    const activeColorBtn = this.card.querySelector('[data-color-option].active');
    if (activeColorBtn) {
      this.selectedColor = activeColorBtn.dataset.colorValue || activeColorBtn.dataset.colorName;
      this.isLinkVariant = activeColorBtn.dataset.isLinkVariant === 'true';
    }
  }

  setupColorButtons() {
    const colorButtons = this.card.querySelectorAll('[data-color-option]');
    
    colorButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleColorChange(btn);
      });
    });
  }

  async handleColorChange(colorBtn) {
    // Atualizar estado ativo
    this.card.querySelectorAll('[data-color-option]').forEach(btn => {
      btn.classList.remove('active');
    });
    colorBtn.classList.add('active');

    this.isLinkVariant = colorBtn.dataset.isLinkVariant === 'true';

    if (this.isLinkVariant) {
      // Variação por link - carregar novo produto
      await this.loadProductVariant(colorBtn.dataset.productHandle);
    } else {
      // Variação tradicional
      this.selectedColor = colorBtn.dataset.colorValue;
      this.updateCardForColor(this.selectedColor);
    }
  }

  async loadProductVariant(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      
      // Atualizar dados do produto
      this.productData = {
        ...product,
        has_variacoes_links: true
      };
      
      // Atualizar imagem
      if (product.featured_image) {
        this.updateCardImage(product.featured_image);
      }
      
      // Atualizar preço
      this.updateCardPrice(product.price, product.compare_at_price);
      
      // Atualizar handle no card
      this.card.dataset.productHandle = handle;
      
    } catch (error) {
      console.error('Error loading product variant:', error);
    }
  }

  updateCardForColor(colorValue) {
    // Encontrar variantes com essa cor
    const colorVariants = this.getVariantsForColor(colorValue);
    
    if (colorVariants.length === 0) return;

    // Atualizar imagem se a primeira variante tiver imagem
    const firstVariant = colorVariants[0];
    if (firstVariant.featured_image) {
      this.updateCardImage(firstVariant.featured_image.src);
    }

    // Atualizar preço
    this.updateCardPrice(firstVariant.price, firstVariant.compare_at_price);
  }

  getVariantsForColor(colorValue) {
    if (!this.productData || !this.productData.variants) return [];
    
    return this.productData.variants.filter(variant => {
      return variant.option1 === colorValue || variant.option2 === colorValue;
    });
  }

  updateCardImage(imageSrc) {
    const img = this.card.querySelector('.shelf-card-main-image');
    if (img) {
      // Construir URL da imagem com tamanho correto
      let newSrc = imageSrc;
      
      // Se a imagem contém CDN do Shopify, usar o filtro de tamanho
      if (imageSrc.includes('cdn.shopify.com')) {
        // Remover parâmetros de tamanho existentes
        newSrc = imageSrc.split('?')[0].replace(/_\d+x\d+(?=\.[^.]+$)/, '');
        
        // Adicionar parâmetros de tamanho
        const ext = newSrc.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        if (ext) {
          newSrc = newSrc.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '_310x457' + ext[0]);
        }
      }
      
      img.src = newSrc;
    }
  }

  updateCardPrice(price, comparePrice) {
    const priceElement = this.card.querySelector('[data-price]');
    const comparePriceElement = this.card.querySelector('[data-compare-price]');
    
    if (priceElement) {
      priceElement.textContent = this.formatMoney(price);
    }
    
    if (comparePriceElement) {
      if (comparePrice && comparePrice > price) {
        comparePriceElement.textContent = this.formatMoney(comparePrice);
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    }

    if(window.innerWidth < 768) {
      this.showSizes();
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    // Fallback simples
    return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
  }

  setupImageHover() {
    const imageContainer = this.card.querySelector('.shelf-card-akag-image');
    const sizesContainer = this.card.querySelector('[data-quick-add-sizes]');
    
    if (!imageContainer || !sizesContainer) return;

    let hoverTimeout;

    imageContainer.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(() => {
        this.showSizes();
      }, 200);
    });

    imageContainer.addEventListener('mouseleave', () => {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(() => {
        this.hideSizes();
      }, 200);
    });

    // Manter aberto quando hover sobre os tamanhos
    sizesContainer.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
    });

    sizesContainer.addEventListener('mouseleave', () => {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(() => {
        this.hideSizes();
      }, 200);
    });

    if(window.innerWidth < 768) {
      this.showSizes();
    }
  }

  showSizes() {
    const overlay = this.card.querySelector('.quick-add-overlay');
    const sizesContainer = this.card.querySelector('[data-quick-add-sizes]');
    
    if (!overlay || !sizesContainer) return;

    // Limpar tamanhos anteriores
    sizesContainer.innerHTML = '';

    if (this.isLinkVariant) {
      // Para variações por link, mostrar todos os tamanhos do produto atual
      this.renderSizesForLinkVariant(sizesContainer);
    } else {
      // Para variações tradicionais, mostrar tamanhos da cor selecionada
      this.renderSizesForColor(sizesContainer);
    }

    overlay.classList.add('active');
  }

  renderSizesForLinkVariant(container) {
    if (!this.productData || !this.productData.variants) return;

    // Adicionar título com nome da cor selecionada
    const selectedColorBtn = this.card.querySelector('[data-color-option].active');
    const colorName = selectedColorBtn ? selectedColorBtn.dataset.colorName : '';

    const title = document.createElement('div');
    title.className = 'quick-add-sizes-title';
    title.innerHTML = colorName 
      ? `<strong>${colorName}</strong><br>Selecione o tamanho:`
      : 'Selecione o tamanho:';
    container.appendChild(title);

    this.productData.variants.forEach(variant => {
      const sizeBtn = this.createSizeButton(
        variant.option1 || variant.title,
        variant.id,
        variant.available
      );
      container.appendChild(sizeBtn);
    });
  }

  renderSizesForColor(container) {
    const colorVariants = this.selectedColor 
      ? this.getVariantsForColor(this.selectedColor)
      : this.productData.variants;

    if (colorVariants.length === 0) return;

    // Adicionar título com nome da cor selecionada
    const selectedColorBtn = this.card.querySelector('[data-color-option].active');
    const colorName = selectedColorBtn ? selectedColorBtn.dataset.colorName : '';

    const title = document.createElement('div');
    title.className = 'quick-add-sizes-title';
    title.innerHTML = colorName 
      ? `<strong>${colorName}</strong><br>Selecione o tamanho:`
      : 'Selecione o tamanho:';
    container.appendChild(title);

    colorVariants.forEach(variant => {
      // Determinar qual option é o tamanho
      const sizeValue = variant.option1 === this.selectedColor 
        ? variant.option2 
        : variant.option1;
      
      const sizeBtn = this.createSizeButton(
        sizeValue || variant.title,
        variant.id,
        variant.available
      );
      container.appendChild(sizeBtn);
    });
  }

  createSizeButton(sizeLabel, variantId, available) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quick-add-size-btn';
    btn.textContent = sizeLabel;
    btn.dataset.variantId = variantId;
    
    if (!available) {
      btn.classList.add('disabled');
      btn.disabled = true;
    } else {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart(variantId);
      });
    }
    
    return btn;
  }

  hideSizes() {
    const overlay = this.card.querySelector('.quick-add-overlay');
    if (overlay) {
      overlay.classList.remove('active');
    }
  }

  async addToCart(variantId) {
    const btn = this.card.querySelector(`[data-variant-id="${variantId}"]`);
    
    if (btn) {
      btn.classList.add('loading');
      // btn.textContent = 'Adicionando...';
    }

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();
      
      // Abrir carrinho lateral
      this.openCartDrawer();
      
      // Atualizar contador do carrinho se existir
      this.updateCartCount();
      
      // Feedback visual
      if (btn) {
        btn.classList.remove('loading');
        btn.classList.add('added');
        // btn.textContent = 'Adicionado!';
        
        setTimeout(() => {
          this.hideSizes();
        }, 500);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      if (btn) {
        btn.classList.remove('loading');
        btn.textContent = 'Erro!';
        
        setTimeout(() => {
          btn.textContent = btn.dataset.originalText || 'Adicionar';
        }, 2000);
      }
    }
  }

  openCartDrawer() {
    // Seguir o mesmo padrão usado no index.min.js (buyButton e buyTogether)
    const openCart = document.querySelector('#cartAction');
    
    fetch(window.Shopify.routes.root + 'cart.js')
      .then((i) => i.json())
      .then((response) => window.renderMinicart(response))
      .then(() => {
        if (openCart) {
          openCart.checked = true;
        }
      })
      .catch((error) => {
        console.error('Erro ao atualizar carrinho:', error);
      });
  }

  async updateCartCount() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();
      
      // Atualizar contador do carrinho
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-count');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
      
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

// Inicializar Quick Add em todos os cards
function initQuickAddCards() {
  const cards = document.querySelectorAll('.shelf-card-akag[data-product-id]');
  cards.forEach(card => {
    new QuickAddCard(card);
  });
}

// Inicializar quando DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickAddCards);
} else {
  initQuickAddCards();
}

// Reinicializar em AJAX/infinite scroll
document.addEventListener('shopify:section:load', initQuickAddCards);
document.addEventListener('quick-add:reinit', initQuickAddCards);

// Observar mudanças no DOM para novos cards
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.classList && node.classList.contains('shelf-card-akag')) {
            shouldReinit = true;
          } else if (node.querySelector && node.querySelector('.shelf-card-akag')) {
            shouldReinit = true;
          }
        }
      });
    });
    
    if (shouldReinit) {
      initQuickAddCards();
    }
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

